import { getDeviceStatus } from '@/components/People/EmployeeProfile/helpers/DevicePropertyHelpers';
import { MDMStatus } from '@/constants/mdmStatus';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import {
  useGetAssetHistoryQuery,
  useGetAssetQuery,
} from '@/redux/slices/assetApiSlice';
import { isDeviceAsset } from '@/types/assets';
import {
  EntitlementSlugEnum,
  ProductTypeName,
} from '@electricjs/core_entity-client';

const MOBILE_PRODUCT_TYPES = new Set<ProductTypeName>([
  ProductTypeName.CellPhone,
  ProductTypeName.OtherPhone,
  ProductTypeName.Tablets,
]);

type UseShowDeviceActionsProps = {
  assetId: string;
};

const useShowDeviceActions = ({ assetId }: UseShowDeviceActionsProps) => {
  const organizationId = useGetOrganizationId();
  const { data: deviceAsset } = useGetAssetQuery(
    {
      organizationId,
      assetId,
    },
    { skip: !assetId }
  );

  const { data: assetHistoryData } = useGetAssetHistoryQuery(
    {
      organizationId,
      assetId,
      limit: 10,
    },
    { skip: !assetId }
  );

  const mdmStatus =
    assetHistoryData && deviceAsset && isDeviceAsset(deviceAsset)
      ? getDeviceStatus({
          assetHistoryData: assetHistoryData.assetHistory,
          deviceAsset: deviceAsset,
        })
      : null;

  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const isManagedExternally = !!deviceAsset?.device?.mdmProviderId;

  const isMobileDevice =
    !!deviceAsset?.productType?.name &&
    MOBILE_PRODUCT_TYPES.has(deviceAsset.productType.name);

  const showDeviceActions =
    !!deviceAsset?.device?.osType &&
    !isMobileDevice &&
    (isManagedExternally ||
      ((mdmStatus === MDMStatus.INSTALLED ||
        mdmStatus === MDMStatus.LOCKED ||
        mdmStatus === MDMStatus.REQUIRES_DESKTOP_APP ||
        mdmStatus === MDMStatus.WIPE_REQUESTED ||
        mdmStatus === MDMStatus.UNENROLL_REQUESTED) &&
        hasMdmEntitlement));

  return showDeviceActions;
};

export default useShowDeviceActions;
